import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@headlessui/react';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import Portal from '../portal';
import { richTextOptions } from '../helpers';

import PlusIcon from '../../icons/icon-plus.svg';
import CloseIcon from '../../icons/icon-close.svg';

const SupportingDocumentCard = ({ title, description, steps = [] }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <div className="w-full bg-white rounded-2xl p-6 u-body-md text-grey-font-1 flex items-center justify-between">
        <div className="max-w-[620px] mr-10">
          <p className="u-h5 mb-2 font-medium">{title}</p>
          <p>{description}</p>
        </div>
        <button
          type="button"
          className="border-2 border-grey-font-1 text-grey-font-1 rounded-xl py-3.5 px-5 u-body-sm font-medium inline-flex items-center gap-x-2.5"
          onClick={() => setIsOpen(true)}
        >
          <span>Expand</span>
          <PlusIcon className="w-2.5 h-2.5" />
        </button>
      </div>
      <Portal>
        <Dialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          className="relative z-50"
        >
          <div className="fixed inset-0 flex w-screen items-center justify-center p-4 bg-opacity-60 bg-blue-navy">
            <Dialog.Panel className="w-full max-w-[1200px] max-h-[calc(100dvh-32px)] lg:max-h-[888px] rounded-2xl text-grey-font-1 bg-white p-6 overflow-y-auto">
              <div className="flex items-center justify-between">
                <div className="max-w-[620px] u-body-md mr-10">
                  <Dialog.Title as="p" className="u-h5 mb-2 font-medium">
                    {title}
                  </Dialog.Title>
                  <Dialog.Description>{description}</Dialog.Description>
                </div>
                <button
                  type="button"
                  className="border-2 border-grey-font-1 text-grey-font-1 rounded-xl py-3.5 px-5 u-body-sm font-medium inline-flex items-center gap-x-2.5"
                  onClick={() => setIsOpen(false)}
                >
                  <span>Close</span>
                  <CloseIcon className="w-4 h-4" />
                </button>
              </div>
              {steps.length > 0 && (
                <div className="mt-16 space-y-10 px-3">
                  {steps.map((step, index) => {
                    const img = getImage(step.image);

                    return (
                      <div
                        className="flex flex-col lg:flex-row lg:items-center"
                        key={`steps-${index}`}
                      >
                        <div className="flex-auto">
                          <hr className="h-[1px] w-[100px] text-[#d9d9d9] mb-2.5" />
                          {step.heading && (
                            <p className="u-body-md text-blue-navy mb-2.5">
                              {step.heading}
                            </p>
                          )}
                          <p className="text-blue-navy u-h4 mb-1 font-medium">
                            {step.title}
                          </p>
                          <div className="text-grey-font-2 u-body-sm space-y-4">
                            {renderRichText(step.body, richTextOptions)}
                          </div>
                        </div>
                        <div className="w-full lg:flex-[0_0_50%] lg:max-w-[50%] lg:ml-10">
                          <div className="aspect-w-16 aspect-h-10">
                            {img && (
                              <div>
                                <GatsbyImage
                                  image={img}
                                  alt={
                                    step.image.description ||
                                    step.image.title ||
                                    ''
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Dialog.Panel>
          </div>
        </Dialog>
      </Portal>
    </>
  );
};

SupportingDocumentCard.defaultProps = {
  steps: [],
};

SupportingDocumentCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      heading: PropTypes.string,
      body: PropTypes.object,
      image: PropTypes.object,
    })
  ),
};

export default SupportingDocumentCard;
